/**
 * SEMANTIC TOKENS — Purpose-Based Design Tokens
 *
 * Every leaf is a `var(--nbx-…)` reference to an NBX semantic token. Names follow Figma
 * (`Text/primary` → `--nbx-text-primary`); the per-mode values, including the SSR and Lock
 * overrides, live in libs/styles/src/tokens/nbx.tokens.json.
 *
 * Token Hierarchy:
 *   nbx.tokens.json → --nbx-* → **Semantic (this file)** → Component Tokens → PrimeNG Presets
 */
import { nbxVar as v } from '../../tokens/generated/nbx-tokens.js';
import type * as a from './aliases.js';
import * as p from './primitives.js';

export const text = {
  primary: v('text.primary'),
  secondary: v('text.secondary'),
  tertiary: v('text.tertiary'),
  muted: v('text.muted'),
  onColor: v('text.on-color'),
  disabled: v('text.disabled'),
  success: v('text.success'),
  danger: v('text.danger'),
  warning: v('text.warning'),
  link: v('text.link'),
  linkHover: v('text.link-hover'),
  linkDisabled: v('text.link-disabled'),
};

export const surface = {
  ground: v('surface.ground'),
  canvas: v('surface.canvas'),
  raised: v('surface.raised'),
  modal: v('surface.modal'),
  disabled: v('surface.disabled'),
  dark: v('surface.dark'),
};

export const border = {
  default: v('border.default'),
  strong: v('border.strong'),
  divider: v('border.divider'),
  focus: v('border.focus'),
  disabled: v('border.disabled'),
  primary: v('border.primary'),
  secondary: v('border.secondary'),
  success: v('border.success'),
  danger: v('border.danger'),
  warning: v('border.warning'),
  focusInput: v('border.focus-input'),
};

export const states = {
  success: {
    bg: v('states.success.bg'),
    fg: v('states.success.fg'),
    border: v('states.success.border'),
  },
  warning: {
    bg: v('states.warning.bg'),
    fg: v('states.warning.fg'),
    border: v('states.warning.border'),
  },
  danger: {
    bg: v('states.danger.bg'),
    fg: v('states.danger.fg'),
    border: v('states.danger.border'),
  },
  info: {
    bg: v('states.info.bg'),
    fg: v('states.info.fg'),
    border: v('states.info.border'),
  },
};

export const focus = {
  ring: v('focus.ring'),
  ringOffset: v('focus.ring-offset'),
};

export const layout = {
  gap: {
    xs: p.spacing[4],
    sm: p.spacing[8],
    md: p.spacing[16],
    lg: p.spacing[24],
    xl: p.spacing[32],
  },
  padding: {
    xs: p.spacing[8],
    sm: p.spacing[24],
    md: p.spacing[48],
    lg: p.spacing[56],
  },
};

export const radius = {
  sm: p.borderRadius[4],
  md: p.borderRadius[8],
  lg: p.borderRadius[16],
  xl: p.borderRadius[24],
  full: v('radius.full'),
};

export const shadow = {
  sm: p.shadows.sm,
  md: p.shadows.md,
  lg: p.shadows.lg,
  card: p.shadows.card,
};

export const typography = {
  fontFamily: {
    base: p.fontFamily.base,
  },
  fontSize: {
    xs: p.fontSize.xs,
    sm: p.fontSize.sm,
    base: p.fontSize.base,
    md: p.fontSize.base,
    lg: p.fontSize.lg,
    xl: p.fontSize.xl,
  },
  fontWeight: {
    normal: p.fontWeight.normal,
    semibold: p.fontWeight.semibold,
    bold: p.fontWeight.bold,
  },
  lineHeight: {
    base: p.lineHeight.base,
    normal: v('line-height.normal'),
    lg: p.lineHeight.lg,
    xl: p.lineHeight.xl,
    '2xl': p.lineHeight['2xl'],
  },
};

export const transition = {
  normal: v('transition.normal'),
  fast: v('transition.fast'),
};

export const iconSizes = {
  base: v('icon-size.base'),
  md: v('icon-size.md'),
  lg: v('icon-size.lg'),
  xl: v('icon-size.xl'),
};

export const controlSize = {
  sm: v('control-size.sm'),
  base: v('control-size.base'),
  md: v('control-size.md'),
  lg: v('control-size.lg'),
};

export const interactive = {
  primary: {
    base: v('interactive.primary.base'),
    hover: v('interactive.primary.hover'),
    pressed: v('interactive.primary.pressed'),
    subtle: v('interactive.primary.subtle'),
    subtlePressed: v('interactive.primary.subtle-pressed'),
  },
  secondary: {
    base: v('interactive.secondary.base'),
    hover: v('interactive.secondary.hover'),
    pressed: v('interactive.secondary.pressed'),
    subtle: v('interactive.secondary.subtle'),
    subtleHover: v('interactive.secondary.subtle-hover'),
    subtlePressed: v('interactive.secondary.subtle-pressed'),
    mid: v('interactive.secondary.mid'),
  },
  success: {
    base: v('interactive.success.base'),
    hover: v('interactive.success.hover'),
    pressed: v('interactive.success.pressed'),
  },
  danger: {
    base: v('interactive.danger.base'),
    hover: v('interactive.danger.hover'),
    pressed: v('interactive.danger.pressed'),
  },
  warning: {
    base: v('interactive.warning.base'),
    hover: v('interactive.warning.hover'),
    pressed: v('interactive.warning.pressed'),
  },
  neutral: {
    base: v('interactive.neutral.base'),
    hover: v('interactive.neutral.hover'),
    subtle: v('interactive.neutral.subtle'),
    subtleHover: v('interactive.neutral.subtle-hover'),
    subtleFocus: v('interactive.neutral.subtle-focus'),
  },
};

export const SEMANTIC = {
  text,
  surface,
  border,
  states,
  focus,
  layout,
  radius,
  shadow,
  typography,
  transition,
  iconSizes,
  controlSize,
  interactive,
};

export type SemanticTokens = typeof SEMANTIC;

export interface AliasSet {
  primary: typeof a.primary;
  secondary: typeof a.secondary;
  surface: typeof a.surface;
  success: typeof a.success;
  warning: typeof a.warning;
  danger: typeof a.danger;
}

type DeepPartial<T> = { [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P] };

export type SemanticOverrides = DeepPartial<SemanticTokens>;

/** Recursively merges `source` into a copy of `target`; arrays and leaves are replaced. */
function deepMerge<T extends Record<string, unknown>>(target: T, source: DeepPartial<T>): T {
  const result = { ...target };
  for (const key of Object.keys(source) as (keyof T)[]) {
    const sv = source[key];
    const tv = target[key];
    if (sv !== undefined && tv !== undefined && typeof sv === 'object' && typeof tv === 'object') {
      result[key] = deepMerge(
        tv as Record<string, unknown>,
        sv as DeepPartial<Record<string, unknown>>
      ) as T[keyof T];
    } else if (sv !== undefined) {
      result[key] = sv as T[keyof T];
    }
  }
  return result;
}

/**
 * The NBX semantic token set, optionally with some leaves replaced. Overrides are expected to
 * be other `var(--nbx-…)` references; literal values bypass the NBX modes.
 */
export function createSemanticTokens(overrides?: SemanticOverrides): SemanticTokens {
  return overrides ? deepMerge(SEMANTIC, overrides) : SEMANTIC;
}
